import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Sprout, Phone, Mail, Shield, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortailClientETALoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({
    eta_nom: 'ETA Sauvayre',
    slogan: 'Travaux agricoles & conseil technique',
    telephone: '',
    email: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/portail-eta/branding-default`)
      .then(r => setBranding(r.data))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/portail-eta/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      localStorage.setItem('portail_eta_token', res.data.access_token);
      localStorage.setItem('portail_eta_client', JSON.stringify(res.data.client));
      toast.success(`Bienvenue dans votre espace ${branding.eta_nom}`);
      navigate('/portail-eta');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Connexion impossible');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: 'linear-gradient(135deg, #022c22 0%, #064e3b 35%, #14532d 70%, #1a2e05 100%)',
      }}
      data-testid="portail-eta-login-page"
    >
      {/* HERO BRANDING ETA en grand */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="max-w-2xl w-full text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-lime-400 via-emerald-400 to-teal-500 shadow-2xl shadow-emerald-500/30 mb-8">
            <Sprout className="w-14 h-14 text-emerald-950" strokeWidth={2.4} />
          </div>

          <p className="text-xs uppercase tracking-[0.4em] text-lime-300/80 mb-3 font-semibold">
            Espace Client
          </p>
          <h1
            className="text-5xl md:text-7xl font-black text-transparent bg-clip-text mb-4 tracking-tight"
            style={{ backgroundImage: 'linear-gradient(135deg, #d9f99d, #6ee7b7, #5eead4)' }}
            data-testid="eta-brand-name"
          >
            {branding.eta_nom}
          </h1>
          <p className="text-emerald-100/80 text-lg md:text-xl max-w-xl mx-auto leading-relaxed">
            {branding.slogan}
          </p>

          {(branding.telephone || branding.email) && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 text-sm text-emerald-200/70">
              {branding.telephone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="w-4 h-4" /> {branding.telephone}
                </span>
              )}
              {branding.email && (
                <span className="flex items-center gap-1.5">
                  <Mail className="w-4 h-4" /> {branding.email}
                </span>
              )}
            </div>
          )}
        </div>

        {/* CARTE LOGIN */}
        <div className="max-w-md w-full">
          <div className="backdrop-blur-2xl bg-white/[0.06] border border-white/10 rounded-3xl p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]">
            <div className="flex items-center gap-2 mb-6">
              <Shield className="w-4 h-4 text-lime-300" />
              <h2 className="text-sm font-semibold text-white tracking-wide">
                Connexion sécurisée
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-emerald-200/70 mb-2">
                  Email
                </label>
                <input
                  data-testid="portail-eta-login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-lime-400/60 focus:border-lime-400/40 transition-all"
                  placeholder="vous@exploitation.fr"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-emerald-200/70 mb-2">
                  Mot de passe
                </label>
                <input
                  data-testid="portail-eta-login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-lime-400/60 focus:border-lime-400/40 transition-all"
                  placeholder="••••••••"
                />
              </div>
              <button
                data-testid="portail-eta-login-submit"
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-lime-400 via-emerald-400 to-teal-400 text-emerald-950 font-bold tracking-wide hover:brightness-110 transition-all shadow-lg shadow-lime-500/20 disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  'Accéder à mon espace'
                )}
              </button>
            </form>

            <p className="text-xs text-white/40 text-center mt-6 leading-relaxed">
              Vos identifiants vous ont été communiqués par <strong className="text-emerald-200/70">{branding.eta_nom}</strong>.
              <br />En cas de perte, contactez directement votre prestataire.
            </p>
          </div>

          <div className="mt-6 text-center">
            <a
              href="/bureau-agricole"
              className="inline-flex items-center gap-2 text-xs text-emerald-300/70 hover:text-lime-300 transition-colors"
              data-testid="link-mba"
            >
              <Sparkles className="w-3 h-3" />
              Découvrir Mon Bureau Agricole, l'ERP qui pilote toute votre exploitation
            </a>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-4 text-center">
        <p className="text-xs text-emerald-200/40">
          Portail propulsé par <strong className="text-emerald-100/70">Mon Bureau Agricole</strong> · Sécurité eIDAS · Données hébergées en France
        </p>
      </footer>
    </div>
  );
}
